import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

const COST_CATEGORIES = ['labour', 'rent', 'utilities', 'energy', 'insurance', 'equipment', 'other']
const ENERGY_BASE_LOAD_PER_MONTH = 400.00

function getMonthStart(date) {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function getMonthsInRange(startMonth, endMonth) {
  const months = []
  const start = new Date(startMonth)
  const end = new Date(endMonth)
  const cursor = new Date(start)
  while (cursor <= end) {
    months.push(getMonthStart(cursor))
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return months
}

/**
 * Fetch fixed costs across a date range.
 * Returns data structured for table display: { months, grid, columnTotals, rowTotals, grandTotal }
 *
 * Energy is computed from energy_monthly_summary (capped at £400/month base load),
 * matching the calculate_fixed_costs() DB function logic.
 *
 * @param {'12m' | 'all'} range - Time range to fetch
 */
export function useFixedCostsRange(range = '12m') {
  const [rawCosts, setRawCosts] = useState([])
  const [energyData, setEnergyData] = useState([])
  const [earliestMonth, setEarliestMonth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // Determine the start month based on range
  const rangeStart = useMemo(() => {
    if (range === 'all' && earliestMonth) {
      return earliestMonth
    }
    // Default: trailing 12 months (11 months back + current)
    const d = new Date()
    d.setMonth(d.getMonth() - 11)
    return getMonthStart(d)
  }, [range, earliestMonth])

  const rangeEnd = useMemo(() => getMonthStart(new Date()), [])

  // For "all" range, first find the earliest month with data
  useEffect(() => {
    if (range !== 'all') return

    async function fetchEarliest() {
      const { data, error: err } = await supabase
        .from('fixed_costs')
        .select('period_start')
        .order('period_start', { ascending: true })
        .limit(1)
        .single()

      if (!err && data) {
        setEarliestMonth(getMonthStart(data.period_start))
      }
    }

    fetchEarliest()
  }, [range])

  // Fetch fixed costs + energy data in parallel
  const fetchCosts = useCallback(async () => {
    if (range === 'all' && !earliestMonth) return

    setLoading(true)
    setError(null)

    try {
      const [costsResult, energyResult] = await Promise.all([
        supabase
          .from('fixed_costs')
          .select('*')
          .gte('period_start', rangeStart)
          .lte('period_start', rangeEnd)
          .order('period_start', { ascending: false })
          .order('category'),
        supabase
          .from('energy_monthly_summary')
          .select('month, total_excl_vat_pence, meter_id')
          .gte('month', rangeStart)
          .lte('month', rangeEnd)
      ])

      if (costsResult.error) throw new Error(costsResult.error.message)
      if (energyResult.error) throw new Error(energyResult.error.message)

      setRawCosts(costsResult.data || [])
      setEnergyData(energyResult.data || [])
    } catch (err) {
      console.error('[useFixedCostsRange] Error:', err)
      setError(err.message)
      setRawCosts([])
      setEnergyData([])
    } finally {
      setLoading(false)
    }
  }, [rangeStart, rangeEnd, range, earliestMonth, refreshKey])

  useEffect(() => {
    fetchCosts()
  }, [fetchCosts])

  // Build table data structure
  const tableData = useMemo(() => {
    const months = getMonthsInRange(rangeStart, rangeEnd).reverse() // newest first

    // grid[monthKey][category] = { total, isXero, isComputed, entries }
    const grid = {}
    for (const month of months) {
      grid[month] = {}
      for (const cat of COST_CATEGORIES) {
        grid[month][cat] = { total: 0, hasXero: false, isComputed: false, entries: [] }
      }
    }

    // Populate from fixed_costs table
    for (const cost of rawCosts) {
      const monthKey = getMonthStart(cost.period_start)
      if (!grid[monthKey]) continue
      const cell = grid[monthKey][cost.category]
      if (!cell) continue
      cell.total += Number(cost.amount) || 0
      cell.entries.push(cost)
      if (cost.source === 'xero') cell.hasXero = true
    }

    // Populate energy from energy_monthly_summary (capped at base load)
    // Group by month first (could have multiple meters)
    const energyByMonth = {}
    for (const row of energyData) {
      const monthKey = getMonthStart(row.month)
      if (!energyByMonth[monthKey]) energyByMonth[monthKey] = 0
      // Cost incl VAT, capped at base load per meter
      const costInclVat = (Number(row.total_excl_vat_pence) / 100) * 1.2
      energyByMonth[monthKey] += Math.min(costInclVat, ENERGY_BASE_LOAD_PER_MONTH)
    }

    for (const [monthKey, amount] of Object.entries(energyByMonth)) {
      if (!grid[monthKey]) continue
      const cell = grid[monthKey].energy
      cell.total = Math.round(amount * 100) / 100
      cell.isComputed = true
    }

    // Row totals (per month)
    const rowTotals = {}
    for (const month of months) {
      rowTotals[month] = COST_CATEGORIES.reduce(
        (sum, cat) => sum + grid[month][cat].total, 0
      )
    }

    // Column totals (per category)
    const columnTotals = {}
    for (const cat of COST_CATEGORIES) {
      columnTotals[cat] = months.reduce(
        (sum, month) => sum + grid[month][cat].total, 0
      )
    }

    const grandTotal = Object.values(rowTotals).reduce((a, b) => a + b, 0)

    return { months, grid, rowTotals, columnTotals, grandTotal }
  }, [rawCosts, energyData, rangeStart, rangeEnd])

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1)
  }, [])

  return {
    ...tableData,
    categories: COST_CATEGORIES,
    isLoading: loading,
    error,
    refresh
  }
}

export default useFixedCostsRange
